import { IMAGE_EXTENSIONS, getFileExtension } from '@odo/shared-ui';

const SUPPORTED_RASTER_EXTENSIONS = IMAGE_EXTENSIONS.filter((ext) => ext !== 'svg');
const THUMBNAIL_MAX_SIZE = 320;
const WEBP_QUALITY = 0.8;

function isRasterImage(file: File): boolean {
    const ext = getFileExtension(file.name);
    return SUPPORTED_RASTER_EXTENSIONS.includes(ext);
}

export async function createImageThumbnail(
    file: File,
    maxSize = THUMBNAIL_MAX_SIZE
): Promise<Blob | null> {
    if (!isRasterImage(file)) return null;

    try {
        const fullBitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const { width: naturalWidth, height: naturalHeight } = fullBitmap;
        fullBitmap.close();

        const scale = Math.min(1, maxSize / Math.max(naturalWidth, naturalHeight));
        const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
        const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

        const bitmap = await createImageBitmap(file, {
            imageOrientation: 'from-image',
            resizeWidth: targetWidth,
            resizeHeight: targetHeight,
            resizeQuality: 'high'
        });

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            bitmap.close();
            return null;
        }

        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();

        return await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/webp', WEBP_QUALITY);
        });
    } catch {
        return null;
    }
}
